/// <reference types="vite/client" />
import '@fontsource-variable/noto-sans/wght-italic.css'
import '@fontsource-variable/noto-sans/wght.css'
import '@fontsource/tilt-neon/400.css'
import '@fontsource/young-serif/400.css'
import {
  Box,
  ColorSchemeScript,
  Group,
  MantineProvider,
  Stack,
  Text,
} from '@mantine/core'
import '@mantine/core/styles.layer.css'
import { Notifications } from '@mantine/notifications'
import '@mantine/notifications/styles.css'
import { ArrowRightIcon } from '@phosphor-icons/react'
import {
  HeadContent,
  Link,
  Outlet,
  Scripts,
  createRootRoute,
  useRouterState,
} from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useEffect } from 'react'
import {
  readBoggleDevModeNavigationMemory,
  resolveBoggleDevMode,
  syncBoggleDevModeFromSearch,
} from '~/boggle/devMode'
import { GamePageHeader } from '~/boggle/ui/chrome/AppLayout'
import { RouteShell } from '~/boggle/ui/chrome/RouteShell'
import { ModeSessionProvider } from '~/boggle/ui/state/ModeSession'
import { loadStandardDictionaryLexicon } from '~/boggle/engine/standard/lexicon'
import { syncViewportZoomLockForTouch } from '../viewportTouchZoom'
import { pathUsesBoggleRouteShell } from '~/routes/boggleRouteShellPaths'
import { shadcnCssVariableResolver } from '~/theme/cssVariableResolver'
import { shadcnTheme } from '~/theme/theme'
import '~/theme/theme.css'
import '../styles.css'
import { ViewportCssVariables } from '~/viewportCssVariables'
import { IconGlyph } from '../boggle/ui/atoms/Icons'
import {
  captureTelemetryException,
  initializeAppTelemetry,
  installTelemetryConsoleHelper,
  setTelemetryTags,
} from '~/telemetry/appTelemetry'

initializeAppTelemetry()
installTelemetryConsoleHelper()

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { name: 'theme-color', content: '#18181b' },
      { name: 'description', content: 'Play Patter — the fast-paced daily word game. Form before time runs out.' },
      { title: 'Patter' },
    ],
    links: [
      { rel: 'icon', href: '/favicon.ico', sizes: 'any' },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicon-32x32.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: '/favicon-16x16.png',
      },
      { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
      { rel: 'manifest', href: '/site.webmanifest' },
    ],
  }),
  component: RootComponent,
  notFoundComponent: RootNotFound,
})

function RootComponent() {
  const location = useRouterState({
    select: state => state.location,
  })
  const pathname = location.pathname
  const search = ((location as { search?: Record<string, unknown> }).search ??
    {}) as Record<string, unknown>
  syncBoggleDevModeFromSearch(search)
  const isDevMode = resolveBoggleDevMode(
    search.dev,
    import.meta.env.DEV,
    readBoggleDevModeNavigationMemory(),
  )
  const useBoggleShell = pathUsesBoggleRouteShell(pathname)

  useEffect(() => {
    void loadStandardDictionaryLexicon().catch(error => {
      captureTelemetryException(error, { feature: 'boggle_dictionary_load' })
    })
  }, [])

  useEffect(() => {
    syncViewportZoomLockForTouch()
  }, [])

  useEffect(() => {
    setTelemetryTags({ route: pathname })
  }, [pathname])

  return (
    <RootDocument>
      <MantineProvider
        defaultColorScheme="dark"
        forceColorScheme="dark"
        theme={shadcnTheme}
        cssVariablesResolver={shadcnCssVariableResolver}
      >
        <ViewportCssVariables />
        <ModeSessionProvider isDevMode={isDevMode}>
          <Notifications pauseResetOnHover="notification" position="top-center" />
          {useBoggleShell ? (
            <RouteShell>
              <Outlet />
            </RouteShell>
          ) : (
            <Outlet />
          )}
        </ModeSessionProvider>
      </MantineProvider>
    </RootDocument>
  )
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html data-mantine-color-scheme="dark" lang="en">
      <head>
        <ColorSchemeScript forceColorScheme="dark" />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  )
}

function RootNotFound() {
  return (
    <Stack gap={0} h="100%" mih={0}>
      <GamePageHeader />
      <Box
        component="article"
        flex={1}
        mih={0}
        px="md"
        style={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '30dvh',
        }}
      >
        <Stack gap={30} align="center">
          <Text>Page not found.</Text>
          <Link to="/">
            <Group gap={10} align="center">
              <Text>Go Home</Text>
              <IconGlyph icon={ArrowRightIcon} size={16} />
            </Group>
          </Link>
        </Stack>
      </Box>
    </Stack>
  )
}
